import { CategoryScale, Chart as ChartJS, ChartOptions, Legend, LinearScale, LineElement, Plugin, PointElement, Title, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import { DataDimension, DateOnly, ReportResponseModel } from "shared/request/myHealthyAdvantageApi";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { ChartColors, lineChartOptions, XAxisSpan } from "./ChartConstants";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { ChartProps } from "./ChartProps";
import { useGetReport } from "./Hooks/useGetReport";
import { formatDimension } from "./dimensionLabelFormatter";
import { useCallback, useEffect, useState } from "react";
import { TimeSeriesLineChartExporter } from "./Export/export";
import { Select } from "shared/UI/Select/Select";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export type LineChartProps = {
  options?: ChartOptions<"line">;
  dimensionLabelFormatter?: (dimension: DataDimension) => string;
  plugins?: Plugin<"line">[];
  xAxisSpan: XAxisSpan;
  showSelect?: boolean;
  selectPlaceholder?: string;
} & ChartProps;

export const LineChart = (chartProps: LineChartProps) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetReport(chartProps);
  const { showSelect, selectPlaceholder, filters, dataExporter, xAxisSpan, colours, dimensionLabelFormatter, options, plugins, translatedLabels } = chartProps;

  const exportData = useCallback(
    () => dataExporter.export(data, filters.dateFrom, filters.dateTo),
    [data, filters, dataExporter]
  );

  const [selectedLine, setSelectedLine] = useState<string | undefined>(undefined);

  useEffect(() => {
    document.addEventListener("exportReport", exportData);
    return () => document.removeEventListener("exportReport", exportData);
  }, [exportData]);

  if (translatedLabels) {
    throw new Error("Translated labels are not supported by line charts");
  }

  if (error) {
    return <ErrorAlert title={t("charts.requestErrorTitle")} content={t("charts.requestErrorBody")} />;
  }

  if (isLoading) {
    return <ViewLoading />;
  }

  if (!data?.dataDimensions?.length) {
    return <p>{t("charts.requestNoData")}</p>;
  }

  const filteredDimensions = showSelect ? data.dataDimensions.filter((d) => d.key === selectedLine) : data.dataDimensions;

  const xAxisDates = getDates(filters.dateFrom, filters.dateTo, xAxisSpan);

  const dataset = mapDataset(
    { ...data, dataDimensions: filteredDimensions },
    xAxisDates,
    colours,
    dimensionLabelFormatter ?? formatDimension
  );

  return (
    <div className="w-full flex flex-col gap-6">
      {
        showSelect && <div className="w-1/2">
          <Select
            aria-label="report-select"
            value={selectedLine}
            onChange={({ target: { value } }) => setSelectedLine(value)}
            placeholder={selectPlaceholder}
          >
            {data.dataDimensions.map((report) => (
              <option key={report.key} value={report.key}>
                {report.key}
              </option>
            ))}
          </Select>
        </div>
      }
      <Line
        data={{
          labels: xAxisDates,
          datasets: dataset,
        }}
        options={options ?? lineChartOptions}
        width={300}
        plugins={plugins}
      />
    </div>
  );
};

function getDates(dateFrom: DateOnly, dateTo: DateOnly, xAxisSpan: XAxisSpan) {
  return Array.from(dateRange(Date.fromDateOnly(dateFrom)!, Date.fromDateOnly(dateTo)!, xAxisSpan));
}

function* dateRange(start: Date, end: Date, xAxisSpan: XAxisSpan): Generator<string> {
  const current = new Date(start);
  const last = new Date(end);

  while (current <= last) {
    yield new Date(current).toLocaleDateString("en-GB");
    current.setDate(current.getDate() + xAxisSpan);
  }
}

function mapDataset(
  data: ReportResponseModel,
  xAxisDates: string[],
  colours: Record<string, string> | undefined,
  dimensionLabelFormatter: (dimension: DataDimension) => string
) {
  return data.dataDimensions!.map((dimension, index) => {
    const colour = colours === undefined ? ChartColors[index] : colours[dimension.key!];

    return {
      label: dimensionLabelFormatter(dimension),
      data: padSeries(xAxisDates, dimension),
      borderColor: colour,
      backgroundColor: colour,
    };
  });
}

function padSeries(xAxisDates: string[], series: DataDimension) {
  return xAxisDates.map((x) => series.value!.find((element) => element.key === x)?.value || 0);
}

LineChart.defaultProps = {
  colours: undefined,
  dataExporter: new TimeSeriesLineChartExporter(),
  translatedLabels: false,
  xAxisSpan: XAxisSpan.weeks,
};
